import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import GetCookie from "../../../../utils/GetCookie";
import authNavigate from "../../../../globalComponents/AuthNav";
import ProcessingPopup from "../../../../utils/popup/ProcessingPopup";
import ErrorPopup from "../../../../utils/popup/ErrorPopup";
import SuccessPopup from "../../../../utils/popup/SuccessPopup";


const ScreeningSpaceSidebar = ({
  slrFabricId,
  fabricIdForTextractor,
  gridDocsThreadId,
  reportDocsThreadId,
  framework,
  abstractScreening,
  fullTextScreening,
  activeView,
  onOpenAbstractList,
  onOpenHumanScreening,
  onOpenAiScreening,
  onOpenConflictResolution,
  onOpenQc,
  onOpenFullTextList,
  onOpenLinkFullText,
  onOpenHumanFT,
  onOpenAiFT,
  onOpenConflictFT,
  onOpenQcFT,
  onOpenUniqueStudies,
  onOpenDataExtraction,
  isCollapsed,
  onToggleCollapse,
}) => {
  const navigate = useNavigate();
  const API_BASE_URL = "/pceapi/slr";

  const headers = useMemo(
    () => ({
      Authorization: GetCookie("access_token"),
    }),
    [],
  );

  const [isAbstractOpen, setIsAbstractOpen] = useState(true);
  const [isFullTextOpen, setIsFullTextOpen] = useState(true);
  const [isGenbiOpen, setIsGenbiOpen] = useState(false);
  const [genbiTemplates, setGenbiTemplates] = useState([]);
  const [loadingGenbi, setLoadingGenbi] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);


  // Show Conflict Resolution:
  // - Always show for DUAL frameworks (DUAL_HUMAN_AI, DUAL_HUMAN_HUMAN)
  // - Show for SINGLE_AI_ONLY only if multiple LLMs are configured (llm_count > 1)
  // - Hide for SINGLE_HUMAN_ONLY (no conflicts possible)
  const abstractLlmCount = abstractScreening?.llm_count ?? 1;
  const fullTextLlmCount = fullTextScreening?.llm_count ?? 1;

  // Processing popup
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] =
    useState(false);
  const [processingMessage, setProcessingMessage] = useState("");

  // Error popup
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState("");

  // Success popup
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState("");

  const showAbstractConflictResolution =
    framework === "SINGLE_AI_ONLY"
      ? abstractLlmCount > 1 // Show only if multiple LLMs in SINGLE_AI_ONLY
      : framework !== "SINGLE_HUMAN_ONLY"; // Show for all DUAL frameworks

  const showFullTextConflictResolution =
    framework === "SINGLE_AI_ONLY"
      ? fullTextLlmCount > 1 // Show only if multiple LLMs in SINGLE_AI_ONLY
      : framework !== "SINGLE_HUMAN_ONLY"; // Show for all DUAL frameworks

  // Show Human Screening:
  // - Project creator: always on a human framework, even with 0 papers
  //   (no allocation needed) -> backend sends show_human_screening = true.
  // - Reviewer: once they have allocated papers (total > 0).
  const showAbstractHumanScreening =
    framework !== "SINGLE_AI_ONLY" &&
    (Boolean(abstractScreening?.show_human_screening) ||
      (abstractScreening?.human_screening?.total ?? 0) > 0);

  const showFullTextHumanScreening =
    framework !== "SINGLE_AI_ONLY" &&
    (Boolean(fullTextScreening?.show_human_screening) ||
      (fullTextScreening?.human_screening?.total ?? 0) > 0);


  const startProcessing = (msg) => {
    setProcessingMessage(msg || "Processing...");
    setIsProcessingPopupVisible(true);
  };

  const stopProcessing = () => {
    setIsProcessingPopupVisible(false);
    setProcessingMessage("");
  };

  const showError = (msg) => {
    setPopupErrorMessage(msg || "Something went wrong");
    setIsErrorPopupVisible(true);
  };

  const showSuccess = (msg) => {
    setPopupSuccessMessage(msg || "Action completed successfully");
    setIsSuccessPopupVisible(true);
  };

  const closeErrorPopup = () => setIsErrorPopupVisible(false);
  const closeSuccessPopup = () => setIsSuccessPopupVisible(false);


  const resolveProgress = (stepKey, stats) => {
    const total = stats?.total ?? 0;

    let done = 0;
    if (!stats) return { done: 0, total: 0 };

    switch (stepKey) {
      case "CONFLICT":
        done = stats.resolved ?? 0;
        break;

      case "LINK_FULL_TEXT":
        done = stats.linked ?? 0;
        break;

      case "QC":
        // QC doesn't have reviewed; infer from included + excluded (cap at total)
        done = Math.min(total, (stats.included ?? 0) + (stats.excluded ?? 0));
        break;

      default:
        // HUMAN / AI screening
        done = stats.reviewed ?? 0;
    }

    return { done, total };
  };

  const fetchGenbiTemplates = async () => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/genbi-templates/list`,
        { slr_fabric_id: slrFabricId },
        { headers },
      );

      if (res.data?.status === "NotAuthorized") {
        authNavigate("/login", "iam", navigate);
        return;
      }

      if (res.data?.status === "success") {
        setGenbiTemplates(res.data.data || []);
      } else {
        showError(res.data?.message || "Failed to load templates");
      }
    } catch (err) {
      console.error("Failed to fetch GenBI templates", err);
      showError("Failed to fetch Gen BI templates");
    } finally {
      stopProcessing();
    }
  };

  const toggleGenbi = () => {
    setIsGenbiOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isGenbiOpen) return;

    startProcessing("Loading Gen BI templates...");
    fetchGenbiTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenbiOpen]);

  const executeOpenTemplate = async (template) => {
    if (!template) return;

    startProcessing("Opening Gen BI workspace...");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/genbi-templates/create-or-open`,
        { slr_genbi_template_id: template.id, slr_fabric_id: slrFabricId },
        { headers },
      );

      if (res.data?.status === "NotAuthorized") {
        authNavigate("/login", "iam", navigate);
        return;
      }

      if (res.data?.status !== "success") {
        showError(res.data?.message || "Failed to open Gen BI template");
        return;
      }

      showSuccess(
        res.data.action === "created"
          ? "Gen BI workspace created"
          : "Gen BI workspace opened",
      );
      const action = res.data.action;
      const templateId = res.data.data?.id || res.data.template_id;

      const fabricId = res.data.data?.fabric_id || res.data.fabric_id;

      console.log("✅ Navigating with", { templateId, fabricId });

      if (!templateId || !fabricId) {
        console.error("❌ Missing IDs from API response", res.data);
        return;
      }

      navigate("/genbi", {
        replace: true,
        state: {
          fromSLR: true,
          slrFabricId: fabricId, // ✅ Pass the GenBI fabric_id (244 in your case)
          slrTemplateName: template.template_name,
          templateId: templateId,
          action: action,
          templateStructure: action === "created" ? res.data.data : null,
          fabricIdForTextractor: fabricIdForTextractor,
          reportDocsThreadId: reportDocsThreadId || null,
        },
      });
    } catch (err) {
      console.error("Failed to open template", err);
      showError("Failed to open Gen BI template");
    } finally {
      stopProcessing();
    }
  };
  const normalizeStatus = (status) => {
    const s = String(status || "").toUpperCase();
    if (!s) return null;

    if (["COMPLETED", "DONE", "SUCCESS"].includes(s)) return "COMPLETED";
    if (["RUNNING", "IN_PROGRESS", "PROCESSING", "STARTED"].includes(s))
      return "RUNNING";
    if (["FAILED", "ERROR"].includes(s)) return "FAILED";

    return s;
  };

  const renderStatusIcon = (status) => {
    const st = normalizeStatus(status);

    if (st === "COMPLETED")
      return <i className="bi bi-check-circle-fill text-primary" />;
    if (st === "RUNNING")
      return <i className="w-5 bi bi-arrow-repeat text-warning" />;
    if (st === "FAILED")
      return <i className="w-5 bi bi-x-circle-fill text-danger" />;

    return <i className="bi bi-circle text-secondary" />;
  };

  const humanAbstractStatus = (() => {
    const t = abstractScreening?.human_screening?.total ?? 0;
    const r = abstractScreening?.human_screening?.reviewed ?? 0;
    if (t === 0) return null;
    return r >= t ? "COMPLETED" : "RUNNING";
  })();

  const humanFullTextStatus = (() => {
    const t = fullTextScreening?.human_screening?.total ?? 0;
    const r = fullTextScreening?.human_screening?.reviewed ?? 0;
    if (t === 0) return null;
    return r >= t ? "COMPLETED" : "RUNNING";
  })();

  // ── COLLAPSED RAIL: text abbreviations per step ──
  const STEP_LABELS = {
    ABSTRACT_LIST: "AL",
    HUMAN_SCREENING: "HS",
    AI_SCREENING: "AI",
    CONFLICT: "CR",
    QC: "QC",
    FULLTEXT_LIST: "FT",
    LINK_FULL_TEXT: "LF",
    HUMAN_FT: "HS",
    AI_FT: "AI",
    CONFLICT_FT: "CR",
    QC_FT: "QC",
    UNIQUE_STUDIES: "US",
    DATA_EXTRACTION: "DE",
    GENBI: "GB",
  };

  const statusToneClass = (status) => {
    const st = normalizeStatus(status);
    if (st === "COMPLETED") return "ev-rail-done";
    if (st === "RUNNING") return "ev-rail-running";
    if (st === "FAILED") return "ev-rail-failed";
    return "ev-rail-idle";
  };

  const renderStep = (stepKey, id, label, stats, status, pillText, onClick) => {
    const isActive = activeView === id;
    const { done, total } = resolveProgress(stepKey, stats);
    const includedCount = stats?.included ?? 0;

    // ── COLLAPSED MODE ──
    if (isCollapsed) {
      return (
        <div
          key={id}
          className={`ev-rail-item avatar-tooltip-right ${isActive ? "ev-rail-active" : ""}`}
          data-tooltip={`${label} — ${done}/${total} — ${pillText}`}
          onClick={onClick}
        >
          <span className={`ev-rail-text ev-bold-med ${statusToneClass(status)}`}>
            {STEP_LABELS[id] || "??"}
          </span>
          {includedCount > 0 && <span className="ev-rail-dot" />}
        </div>
      );
    }

    // ── EXPANDED MODE ──
    return (
      <div
        className="d-flex align-items-center mb-2 cursor-pointer"
        onClick={onClick}
      >
        <div className="me-2 w-5 text-center">{renderStatusIcon(status)}</div>

        {/* Card: Changes style if active */}
        <div
          className={`w-100 d-flex justify-content-between align-items-center px-3 py-1 transition-all ev-radius-small 
          ${isActive ? "ev-card-2 active-blue" : "ev-card-2"}`}
        >
          <span
            className={
              isActive
                ? "ev-small-p-light ev-bold-med"
                : "ev-small-p-blue ev-bold-med"
            }
          >
            {label}
          </span>

          {stats && (
            <div className="d-flex flex-column align-items-end gap-1" style={{ minHeight: "var(--px-45)" }}>
              {/* TOP PILL */}
              {stepKey !== "QC" && (
                <div className="w-100 text-center" style={{ minWidth: "92px" }}>
                  <div className="ev-message-card6 ev-btn-xsmall w-100 text-center">
                    {done} / {total}
                  </div>
                </div>
              )}

              {/* BOTTOM PILL */}
              {pillText && (
                <div className="w-100 text-center" style={{ minWidth: "92px" }}>
                  <div className="category-badge category-badge-6 ev-btn-xsmall w-100 text-center mb-0">
                    {pillText}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── SHARED STEP GROUPS (used by both expanded and collapsed modes) ──
  const renderAbstractSteps = () => (
          <>
            {showAbstractHumanScreening &&
              renderStep(
                "HUMAN",
                "HUMAN_SCREENING",
                "Human Screening",
                abstractScreening?.human_screening,
                humanAbstractStatus,
                `${abstractScreening?.human_screening?.included ?? 0} Included`,
                onOpenHumanScreening,
              )}

            {framework !== "SINGLE_HUMAN_ONLY" &&
              framework !== "DUAL_HUMAN_HUMAN" &&
              renderStep(
                "AI",
                "AI_SCREENING",
                "AI Screening",
                abstractScreening?.ai_screening,
                abstractScreening?.statuses?.ai_status,
                `${abstractScreening?.ai_screening?.included} Included`,
                onOpenAiScreening,
              )}

            {showAbstractConflictResolution &&
              renderStep(
                "CONFLICT",
                "CONFLICT",
                "Conflict Resolution",
                abstractScreening?.conflict_resolution,
                abstractScreening?.statuses?.conflict_resolution_status,
                `${abstractScreening?.conflict_resolution?.resolved ?? 0} Resolved`,
                onOpenConflictResolution,
              )}

            {renderStep(
              "QC",
              "QC",
              "QC",
              abstractScreening?.qc,
              abstractScreening?.statuses?.qc_status,
              `${abstractScreening?.qc?.included} Included`,
              onOpenQc,
            )}
          </>
  );

  const renderFullTextSteps = () => (
          <>
            {renderStep(
              "LINK_FULL_TEXT",
              "LINK_FULL_TEXT",
              "Map Full Text",
              fullTextScreening?.link_full_text,
              fullTextScreening?.statuses?.linking_status,
              `${fullTextScreening?.link_full_text?.linked ?? 0} Linked`,
              onOpenLinkFullText,
            )}

            {showFullTextHumanScreening &&
              renderStep(
                "HUMAN_FT",
                "HUMAN_FT",
                "Human Screening",
                fullTextScreening?.human_screening,
                humanFullTextStatus,
                `${fullTextScreening?.human_screening?.included ?? 0} Included`,
                onOpenHumanFT,
              )}

            {framework !== "SINGLE_HUMAN_ONLY" &&
              framework !== "DUAL_HUMAN_HUMAN" &&
              renderStep(
                "AI_FT",
                "AI_FT",
                "AI Screening",
                fullTextScreening?.ai_screening,
                fullTextScreening?.statuses?.ai_status,
                `${fullTextScreening?.ai_screening?.included ?? 0} Included`,
                onOpenAiFT,
              )}

            {showFullTextConflictResolution &&
              renderStep(
                "CONFLICT",
                "CONFLICT_FT",
                "Conflict Resolution",
                fullTextScreening?.conflict_resolution,
                fullTextScreening?.statuses?.conflict_resolution_status,
                `${fullTextScreening?.conflict_resolution?.resolved ?? 0} Resolved`,
                onOpenConflictFT,
              )}

            {renderStep(
              "QC",
              "QC_FT",
              "QC",
              fullTextScreening?.qc,
              fullTextScreening?.statuses?.qc_status,
              `${fullTextScreening?.qc?.included ?? 0} Included`,
              onOpenQcFT,
            )}

            {renderStep(
              "QC",
              "UNIQUE_STUDIES",
              "Unique Studies",
              {
                total: (abstractScreening?.qc?.included ?? 0) + (fullTextScreening?.qc?.included ?? 0),
                included: (abstractScreening?.qc?.included ?? 0) + (fullTextScreening?.qc?.included ?? 0),
                excluded: 0
              },
              null,
              `${(fullTextScreening?.qc?.included ?? 0)} Included`,
              onOpenUniqueStudies,
            )}
          </>
  );

  // ── COLLAPSED: plain action row (list buttons / bottom actions) ──
  const renderCollapsedAction = (label, tooltip, onClick, isActive = false) => (
    <div
      className={`ev-rail-item avatar-tooltip-right ${isActive ? "ev-rail-active" : ""}`}
      data-tooltip={tooltip}
      onClick={onClick}
    >
      <span className="ev-rail-text ev-bold-med ev-xsmall-p-blue">{label}</span>
    </div>
  );

  return (
    <div className={`ev-card-4 vh-80 d-flex flex-column ${isCollapsed ? "ev-sidebar-collapsed" : "ev-padding-med py-2 ps-2 pe-1"}`}>
      <div
        className={`ev-radius-xsmall d-flex align-items-center justify-content-between mb-1 cursor-pointer gap-2 ${isCollapsed ? "avatar-tooltip-right justify-content-center ms-2" : ""}`}
        data-tooltip={isCollapsed ? "Expand Review Pipeline" : undefined}
        onClick={onToggleCollapse}
      >
        {!isCollapsed && (
          <>
            <div></div>
            <p className="ev-med-p-dark-blue ev-bold-med mb-0">Review Pipeline</p>
          </>
        )}
        <i className={`bi ${isCollapsed ? "bi-layout-sidebar px-1" : "bi-chevron-left"} ev-med-p-dark-blue`}></i>
      </div>

      {/* ================= COLLAPSED ICON RAIL ================= */}
      {isCollapsed && (
        <div className="vh-80 ev-rail">
          {/* Abstract Screening */}
          {renderCollapsedAction(
            STEP_LABELS.ABSTRACT_LIST,
            "Abstracts List",
            onOpenAbstractList,
            activeView === "ABSTRACT_LIST",
          )}
          {renderAbstractSteps()}

          <div className="ev-rail-divider" />

          {/* Full Text Screening */}
          {renderCollapsedAction(
            STEP_LABELS.FULLTEXT_LIST,
            "Full Texts List",
            onOpenFullTextList,
            activeView === "FULLTEXT_LIST",
          )}
          {renderFullTextSteps()}

          <div className="ev-rail-divider" />

          {/* Bottom actions  */}
          {/* {renderCollapsedAction(
            STEP_LABELS.DATA_EXTRACTION,
            "Data Extraction",
            onOpenDataExtraction,
            activeView === "DATA_EXTRACTION",
          )} */}

          {/* {renderCollapsedAction(STEP_LABELS.GENBI, "Gen BI Synthesis", () => {
            setIsGenbiOpen(true);
            onToggleCollapse();
          })} */}
        </div>
      )}

      {!isCollapsed && (
      <div className="vh-80 scrollable-element pe-2">
        {/* ================= ABSTRACT SCREENING ================= */}
        <div
          className="ev-message-card3 d-flex justify-content-between align-items-center mb-2 ev-radius-xsmall cursor-pointer"
          onClick={() => setIsAbstractOpen((prev) => !prev)}
        >
          <div className="d-flex align-items-center gap-2">
            <i
              className={`bi ${isAbstractOpen ? "bi-chevron-down" : "bi-chevron-right"}`}
            />
            <span className="ev-small-p-blue ev-bold-med">
              Abstract Screening
            </span>
          </div>

          <button
            type="button"
            className={`ev-btn-small ev-radius-xsmall py-1 ps-2 pe-2 ${activeView === "ABSTRACT_LIST" ? "ev-btn-dark-blue-fill-round" : "ev-btn-white-fill-round "}`}
            onClick={(e) => {
              e.stopPropagation();
              onOpenAbstractList();
            }}
          >
            Abstracts List
          </button>
        </div>

        {isAbstractOpen && renderAbstractSteps()}

        {/* ================= FULL TEXT SCREENING ================= */}
        <div
          className="ev-message-card3 d-flex justify-content-between align-items-center mt-2 mb-2 ev-radius-xsmall cursor-pointer"
          onClick={() => setIsFullTextOpen((prev) => !prev)}
        >
          <div className="d-flex align-items-center gap-2">
            <i
              className={`bi ${isFullTextOpen ? "bi-chevron-down" : "bi-chevron-right"}`}
            />
            <span className="ev-small-p-blue ev-bold-med">
              Full Text Screening
            </span>
          </div>

          <button
            type="button"
            className={`ev-btn-small ev-radius-xsmall py-1 ps-2 pe-2 ${activeView === "FULLTEXT_LIST" ? "ev-btn-dark-blue-fill-round" : "ev-btn-white-fill-round"}`}
            onClick={(e) => {
              e.stopPropagation();
              onOpenFullTextList();
            }}
          >
            Full Texts List
          </button>
        </div>

        {isFullTextOpen && renderFullTextSteps()}

        {/* ================= DATA EXTRACTION =================
        Reachable only from Project Overview's step 3 card.
        <div className="mt-3 d-flex flex-column gap-2">
          <button
            type="button"
            className={`ev-btn-sidebar-pill ${activeView === "DATA_EXTRACTION" ? "active" : ""}`}
            onClick={onOpenDataExtraction}
          >
            <span className="ev-small-p-dark ev-bold-med">Data Extraction</span>
            <i className="bi bi-table"></i>
          </button>
        </div>
        */}
      </div>
      )}

      {/* Processing Popup */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}

      {/* Error Popup */}
      {isErrorPopupVisible && (
        <ErrorPopup onClose={closeErrorPopup} message={popupErrorMessage} />
      )}

      {/* Success Popup */}
      {isSuccessPopupVisible && (
        <SuccessPopup
          onClose={closeSuccessPopup}
          message={popupSuccessMessage}
        />
      )}
    </div>
  );
};

export default ScreeningSpaceSidebar;